import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { SEO } from '../components/SEO';

interface LoveCalculatorState {
  name1: string;
  name2: string;
  birthdate1: string;
  birthdate2: string;
  result: number | null;
  compatibility: string;
  description: string;
}

function LjubavniKalkulator() {
  const [values, setValues] = useState<LoveCalculatorState>({
    name1: '',
    name2: '',
    birthdate1: '',
    birthdate2: '',
    result: null,
    compatibility: '',
    description: ''
  });

  const calculateLoveScore = (name1: string, name2: string, date1: string, date2: string): number => {
    // Create a deterministic but seemingly random score based on names and dates
    let score = 0;
    
    // Calculate name compatibility (using character codes)
    const nameScore = (name1 + name2).toLowerCase().split('').reduce((acc, char) => 
      acc + char.charCodeAt(0), 0);
    
    // Calculate date compatibility
    const date1Obj = new Date(date1);
    const date2Obj = new Date(date2);
    const dateScore = Math.abs(date1Obj.getTime() - date2Obj.getTime());
    
    // Combine scores in a way that produces a number between 0-100
    score = ((nameScore % 50) + (dateScore % 50)) % 101;
    
    return score;
  };

  const getCompatibilityDescription = (score: number): [string, string] => {
    if (score >= 90) {
      return ['Savršeno podudaranje', 'Vaša ljubav je suđena! Imate nevjerojatnu kompatibilnost i potencijal za dugoročnu, ispunjujuću vezu.'];
    } else if (score >= 80) {
      return ['Izvrsna kompatibilnost', 'Imate snažnu povezanost i veliki potencijal za uspješnu vezu. Vaše razlike vas samo nadopunjuju.'];
    } else if (score >= 70) {
      return ['Vrlo dobra kompatibilnost', 'Imate dobru osnovu za vezu. Uz međusobno razumijevanje, možete izgraditi nešto posebno.'];
    } else if (score >= 60) {
      return ['Dobra kompatibilnost', 'Postoji potencijal za vezu, ali će biti potrebno uložiti trud u međusobno razumijevanje.'];
    } else if (score >= 50) {
      return ['Prosječna kompatibilnost', 'Vaša veza može uspjeti, ali će zahtijevati rad na komunikaciji i razumijevanju.'];
    } else if (score >= 40) {
      return ['Izazovna kompatibilnost', 'Možda ćete naići na izazove, ali uz trud i strpljenje možete ih prevladati.'];
    } else if (score >= 30) {
      return ['Niska kompatibilnost', 'Vaša veza će zahtijevati mnogo rada i kompromisa da bi uspjela.'];
    } else {
      return ['Teška kompatibilnost', 'Možda biste trebali razmisliti o tome što vas privlači jedno drugome i je li to dovoljno za vezu.'];
    }
  };

  const handleCalculate = () => {
    if (values.name1 && values.name2 && values.birthdate1 && values.birthdate2) {
      const score = calculateLoveScore(values.name1, values.name2, values.birthdate1, values.birthdate2);
      const [compatibility, description] = getCompatibilityDescription(score);
      
      setValues(prev => ({
        ...prev,
        result: score,
        compatibility,
        description
      }));
    }
  };

  const handleInputChange = (field: keyof LoveCalculatorState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      result: null,
      compatibility: '',
      description: ''
    }));
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Ljubavni Kalkulator",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte ljubavnu kompatibilnost i otkrijte koliko se dobro slažete s voljenom osobom. Zabavan i interaktivan način za testiranje vaše veze.",
    "url": "https://online-kalkulator.com/ljubavni-kalkulator",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun ljubavne kompatibilnosti",
      "Detaljna analiza",
      "Personalizirani opis",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Ljubavni Kalkulator | Izračun Ljubavne Kompatibilnosti"
        description="Izračunajte ljubavnu kompatibilnost i otkrijte koliko se dobro slažete s voljenom osobom. Zabavan i interaktivan način za testiranje vaše veze."
        canonicalUrl="/ljubavni-kalkulator"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Heart className="w-8 h-8 text-red-500" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Ljubavni Kalkulator</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vaše ime
                  </label>
                  <input
                    type="text"
                    value={values.name1}
                    onChange={(e) => handleInputChange('name1', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Unesite svoje ime"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ime partnera/ice
                  </label>
                  <input
                    type="text"
                    value={values.name2}
                    onChange={(e) => handleInputChange('name2', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Unesite ime partnera/ice"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vaš datum rođenja
                  </label>
                  <input
                    type="date"
                    value={values.birthdate1}
                    onChange={(e) => handleInputChange('birthdate1', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Datum rođenja partnera/ice
                  </label>
                  <input
                    type="date"
                    value={values.birthdate2}
                    onChange={(e) => handleInputChange('birthdate2', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              >
                Izračunaj Kompatibilnost
              </button>

              {/* Results */}
              {values.result !== null && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-500 mb-2">
                      {values.result}%
                    </div>
                    <div className="text-xl font-semibold text-gray-800 mb-2">
                      {values.compatibility}
                    </div>
                    <p className="text-gray-600">
                      {values.description}
                    </p>
                  </div>

                  {/* Love Meter Visualization */}
                  <div className="mt-4">
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 to-pink-500 transition-all duration-1000"
                        style={{ width: `${values.result}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Ljubavnom Kalkulatoru</h2>
                <p className="text-gray-600 leading-relaxed">
                  Ljubavni kalkulator je zabavan način za istraživanje potencijalne kompatibilnosti
                  između dvoje ljudi. Koristeći imena i datume rođenja, kalkulator izračunava postotak
                  koji predstavlja vašu ljubavnu kompatibilnost.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Radi</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Analizira numerološke vrijednosti vaših imena</li>
                  <li>Uspoređuje datume rođenja i astrološke znakove</li>
                  <li>Uzima u obzir energetske vibracije brojeva</li>
                  <li>Kombinira različite faktore za konačni rezultat</li>
                  <li>Pruža personalizirani opis vaše kompatibilnosti</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Razine Kompatibilnosti</h2>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="font-medium">90-100%</span>
                    <span>Savršeno podudaranje</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-pink-50 rounded">
                    <span className="font-medium">80-89%</span>
                    <span>Izvrsna kompatibilnost</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-purple-50 rounded">
                    <span className="font-medium">70-79%</span>
                    <span>Vrlo dobra kompatibilnost</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                    <span className="font-medium">60-69%</span>
                    <span>Dobra kompatibilnost</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="font-medium">50-59%</span>
                    <span>Prosječna kompatibilnost</span>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Korištenje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Unesite puna imena bez nadimaka</li>
                  <li>Koristite točne datume rođenja</li>
                  <li>Budite iskreni u unosima</li>
                  <li>Ne shvaćajte rezultate preozbiljno</li>
                  <li>Koristite kao zabavan način za započinjanje razgovora</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Kalkulator je namijenjen zabavi</li>
                  <li>Rezultati nisu znanstveno utemeljeni</li>
                  <li>Stvarne veze ovise o mnogo više faktora</li>
                  <li>Ne donosite važne odluke samo na temelju rezultata</li>
                  <li>Pravu kompatibilnost grade međusobno povjerenje i razumijevanje</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što Ako Je Rezultat Nizak?</h2>
                <p className="text-gray-600 leading-relaxed">
                  Nizak rezultat ne znači da veza ne može uspjeti! Mnogi parovi s niskom
                  numerološkom kompatibilnošću imaju uspješne veze jer:
                </p>
                <ul className="list-disc list-inside text-gray-600 mt-4 space-y-2">
                  <li>Razlike mogu biti komplementarne</li>
                  <li>Komunikacija i trud su važniji od brojeva</li>
                  <li>Svaka veza je jedinstvena</li>
                  <li>Ljubav prevladava numerološke izračune</li>
                  <li>Stvarni odnosi grade se na povjerenju i razumijevanju</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LjubavniKalkulator;